import { useEffect, useRef } from 'react';

import { createMarkerContent } from './markerContent';

interface AdvancedMarkerProps {
  map: google.maps.Map | null;
  position: google.maps.LatLngLiteral;
  icon?: google.maps.Icon | string;
  title?: string;
  zIndex?: number;
  onClick?: () => void;
  // eslint-disable-next-line no-unused-vars
  onLoad?: (marker: google.maps.marker.AdvancedMarkerElement) => void;
  onUnmount?: () => void;
}

export default function AdvancedMarker({
  map,
  position,
  icon,
  title,
  zIndex,
  onClick,
  onLoad,
  onUnmount
}: AdvancedMarkerProps) {
  const markerRef = useRef<google.maps.marker.AdvancedMarkerElement | null>(null);
  const onClickRef = useRef(onClick);
  const onLoadRef = useRef(onLoad);
  const onUnmountRef = useRef(onUnmount);
  onClickRef.current = onClick;
  onLoadRef.current = onLoad;
  onUnmountRef.current = onUnmount;

  useEffect(() => {
    if (!map || typeof google === 'undefined' || !google.maps?.marker?.AdvancedMarkerElement) {
      return undefined;
    }

    const marker = new google.maps.marker.AdvancedMarkerElement({
      map,
      position,
      content: createMarkerContent(icon),
      title,
      zIndex
    });
    markerRef.current = marker;
    onLoadRef.current?.(marker);

    const clickListener = marker.addListener('click', () => {
      onClickRef.current?.();
    });

    return () => {
      clickListener.remove();
      marker.map = null;
      markerRef.current = null;
      onUnmountRef.current?.();
    };
  }, [map, icon, title, zIndex]);

  useEffect(() => {
    if (markerRef.current) {
      markerRef.current.position = position;
    }
  }, [position.lat, position.lng]);

  return null;
}

AdvancedMarker.defaultProps = {
  icon: undefined,
  title: undefined,
  zIndex: undefined,
  onClick: undefined,
  onLoad: undefined,
  onUnmount: undefined
};
